import { useEffect, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { setAuthToken, clearAuthToken, getAuthToken, verifyAuth } from "@/lib/journal";

export const LoginGate = ({ children }) => {
  const [state, setState] = useState("checking");
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!getAuthToken()) return setState("locked");
      try {
        await verifyAuth();
        setState("open");
      } catch {
        clearAuthToken();
        setState("locked");
      }
    };
    check();
    const onUnauthorized = () => setState("locked");
    window.addEventListener("tj13-unauthorized", onUnauthorized);
    return () => window.removeEventListener("tj13-unauthorized", onUnauthorized);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setAuthToken(user.trim(), password);
    try {
      await verifyAuth();
      setPassword("");
      setState("open");
    } catch {
      clearAuthToken();
      setError("Invalid username or password");
    } finally {
      setBusy(false);
    }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-loading">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (state === "locked") {
    return (
      <div className="min-h-screen bg-background flex items-center px-6" data-testid="login-screen">
        <div className="w-full max-w-sm ml-0 sm:ml-24">
          <div className="flex items-center gap-2 text-primary mb-10">
            <Lock className="h-4 w-4" />
            <span className="text-xs tracking-[0.35em] uppercase">Restricted</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-foreground">TJ13</h1>
          <p className="text-sm text-muted-foreground mt-2 mb-10">by ALVO 13 — private trading journal</p>

          <form onSubmit={submit} className="space-y-3">
            <Input
              autoFocus
              placeholder="Username"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              data-testid="login-username-input"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
            />
            {error && (
              <p className="text-xs text-destructive" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return children;
};

export default LoginGate;
